import type { RoleRef } from '@dawn/feature-config';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/hooks/queries/query-keys';
import { isAlreadyChangedError } from '@/lib/admin-action-error';
import { adminsApi } from '@/lib/api';

/**
 * Replace an admin's roles (PUT /admins/:id/roles, TDEV-1213). On success, and on
 * a 404/409 that means the roster moved, we refetch the roster and the Users
 * page's list (both show roles) so they reflect server truth. The refetch is
 * awaited: the dialog closes on the save promise and reopens from the row the
 * table holds, so settling before the roster is fresh would reopen on old roles.
 */
export function useUpdateAdminRoles() {
  const queryClient = useQueryClient();
  const refreshRoster = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.admins.roster }),
      queryClient.invalidateQueries({ queryKey: queryKeys.admins.list }),
    ]);
  return useMutation<
    { id: string; roles: RoleRef[] },
    Error,
    { adminId: string; roleIds: string[] }
  >({
    mutationFn: ({ adminId, roleIds }) => adminsApi.updateAdminRoles(adminId, roleIds),
    onSuccess: refreshRoster,
    onError: (error) => (isAlreadyChangedError(error) ? refreshRoster() : undefined),
  });
}
